<script>
import { ref, computed } from "vue";
import { useLandingPageStore } from "../../../stores/landingPage";
import { useRoute } from "vue-router";
import Icon from "../../components/Icon.vue";
import ResourceDisplay from "../../components/ResourceDisplay.vue";
import BaseModal from "../../components/BaseModal.vue";

export default {
  name: "ImagePicker",
  components: {BaseModal, ResourceDisplay, Icon },
  props: {
    modelValue: {
      type: [Number, String, null],
      default: null,
    },
    clearOption: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", 'open-uploader', 'image-deleted'],
  setup() {
    const route = useRoute();
    const landingStore = useLandingPageStore();
    const openConfirmation = ref(false);
    const resourceToDelete = ref(null);
    const manageImagesPressed = ref(false);
    const showRemoveIcons = ref(false);

    return {
      route,
      landingStore,
      openConfirmation,
      resourceToDelete,
      manageImagesPressed,
      showRemoveIcons
    };
  },

  computed: {
    resources() {
      return this.landingStore.resources;
    },

  },

  methods: {
    removeImagesOn(){
      this.showRemoveIcons = true;
    },
    removeImagesOff(){
      this.showRemoveIcons = false;
    },
    openRemoveConfirmation(resource){
      this.resourceToDelete = resource;
      this.openConfirmation = true;
    },
    onResourceClick(resource) {
      if (this.showRemoveIcons) return;
      this.$emit("update:modelValue", resource?.id);
    },
    removeImage() {
        // console.log('Removing resource:', this.resourceToDelete);
        this.landingStore.removeImage(this.resourceToDelete);
        this.openConfirmation = false;
        this.resourceToDelete = null;
    },
    imageDimensionsStyling(resource) {
      // get thumbnail dimensions in css
      const data = resource.image_metadata?.thumbnail;
      const height = data?.height || 100;
      return `height: ${height}px;`;
    },
    imageInUse(resourceId){
      const layout = this.landingStore.draftLayout;
      const brand = this.landingStore.localBrand;

      if (brand.logo?.id === resourceId || brand.favicon?.id === resourceId) {
        return true;
      }

      const landingPage = layout?.pages?.find(p => p.page_type === 'landing_page');
      if (!landingPage) return false;

      return landingPage.components?.some(component => {
        const data = component.component_data;
        return data?.bannerImage?.resourceId === resourceId ||
               data?.articleImage?.resourceId === resourceId;
      });
    }
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex flex-between items-center justify-start gap-4 px-6 py-4 bg-white border-b">
      <!-- Search Field -->
      <button
        class="px-4 py-2 text-white rounded-lg upload-new-btn"
        @click="$emit('open-uploader')"
      >
        Upload New
      </button>
      <button
        v-if="!showRemoveIcons"
        class="px-4 py-2 text-white rounded-lg upload-new-btn"
        @click="removeImagesOn"
      >
        Delete Images
      </button>
      <button
        v-if="showRemoveIcons"
        class="px-4 py-2 text-white rounded-lg upload-new-btn"
        @click="removeImagesOff"
      >
        Done
      </button>
    </div>

    <!-- Masonry Grid of Images -->
    <div class="px-6 py-4 bg-gray-50">
      <div class="columns-4 gap-4">
        <!-- “Clear” card -->
        <div
          v-if="clearOption"
          class="relative w-full mb-0 cursor-pointer overflow-hidden rounded-lg bg-white border border-transparent shadow-sm transition hover:border-red-400 hover:shadow-lg"
          style="break-inside: avoid;"
          @click="onResourceClick(null)"
        >
          <div class="flex h-40 items-center justify-center">
            <icon
              icon="ban"
              size="40px"
              class="text-red-500"
            />
          </div>
          <div class="absolute bottom-2 left-1/2 transform -translate-x-1/2">
            <span class="text-sm font-medium text-red-600">Clear</span>
          </div>
        </div>

        <!-- Loop over filtered images -->
        <div
          v-for="resource in resources"
          :key="resource.id"
          class="relative mb-4 rounded-lg bg-white shadow-sm transition hover:shadow-lg"
          :style="`break-inside: avoid; cursor: ${showRemoveIcons ? 'default' : 'pointer'}; margin-bottom: 1rem;`"
          @click="onResourceClick(resource)"
        >
          <!-- Thumbnail itself -->
          <resource-display
            class="min-h-20"
            :resource-id="resource.id"
          />

          <!-- Very subtle dark overlay on hover -->
          <div
            class="absolute inset-0 bg-black bg-opacity-0 transition-opacity hover:bg-opacity-10"
          />

          <!-- Checkmark badge if selected -->
          <div
            v-if="modelValue === resource.id"
            class="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white"
          >
            <icon
              icon="check"
              size="14px"
              class="text-white"
            />
          </div>

          <!-- Delete button -->
          <div v-if="showRemoveIcons ">
            <div
              v-if="modelValue !== resource.id && !imageInUse(resource.id)"
              class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-white  hover:bg-opacity-100 cursor-pointer transition-opacity"
              @click.stop="openRemoveConfirmation(resource)"
            >
              <icon
                icon="trash"
                size="16px"
                color="red"
              />
            </div>

            <!-- Gray overlay for images in use -->
            <div
              v-if="imageInUse(resource.id)"
              class="absolute inset-0 bg-gray-500 bg-opacity-50 hover:bg-opacity-70 flex items-center justify-center image-in-use-overlay"
            >
              <span class="image-in-use-text">Image in use <br>(Cannot Delete) </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <BaseModal
    v-if="openConfirmation"
    title="Delete Image?"
    size="small"
    @close="openConfirmation = false"
  >
    <template #default>
      <p>Are you sure you want to delete this image?</p>
      <div class="mt-4 flex justify-end gap-2">
        <button
          class="btn-filled"
          @click="removeImage()"
        >
          Delete
        </button>
        <button
          class="btn-outline"
          @click="openConfirmation = false"
        >
          Cancel
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.columns-4 > * {
  break-inside: avoid;
}

.upload-new-btn {
  @apply bg-default_secondary_dark text-white;
}

.image-in-use-overlay {
  border-radius: 0.5rem;
}

.image-in-use-text {
  opacity: 0;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  transition: opacity 0.2s;
  text-align: center;
}

.image-in-use-overlay:hover .image-in-use-text {
  opacity: 1;
}

:deep(.base-modal-container p){
  font-size: 1rem;
}
</style>
